import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import {
  FONT,
  NAVY,
  ML,
  thb,
  fmtD,
  drawSectionTitle,
  drawField,
  ensureSpace,
  resolveStart,
  daysFrom,
  calcInterest,
  DEFAULT_INTEREST_RATE,
} from "./pdfHelpers";

function drawSubTitle(doc, x, y, text) {
  doc.setFont(FONT, "bold");
  doc.setFontSize(14);
  doc.setTextColor(NAVY[0], NAVY[1], NAVY[2]);
  doc.text(text, x, y);
  doc.setTextColor(0, 0, 0);
  return y + 5;
}

function drawLabelValueTable(doc, y, rows) {
  autoTable(doc, {
    startY: y,
    margin: { left: ML, right: ML },
    theme: "grid",
    styles: {
      font: FONT,
      fontSize: 13,
      textColor: [0, 0, 0],
      lineColor: [80, 80, 80],
      lineWidth: 0.2,
    },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 50, fillColor: [245, 245, 245] },
    },
    body: rows,
  });
  return doc.lastAutoTable.finalY + 7;
}

const fmtDate = (s) => {
  if (!s) return "-";
  const [mm, dd, yy] = s.split("-");
  return `20${yy}-${mm}-${dd}`;
};

export function generateLawyerReportPdf({
  result,
  accounts,
  detail,
  contractNo,
  contracts,
  calcDate,
  startDate,
  interestRate = DEFAULT_INTEREST_RATE,
  item,
  operatorNote,
}) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const MR = pageW - ML;
  const contentW = MR - ML;

  const phones = detail?.customer_phones ?? [];
  const collaterals = detail?.stock_collaterals ?? [];
  const nationalId = detail?.customers?.[0]?.citizen_id;
  // วันที่คิดดอกเบี้ยถึง (ส่งมาจาก DatePicker, ไม่ส่งมาใช้วันนี้)
  const asOf = calcDate ?? dayjs();
  // map account_no → overdue (ใช้หา due_date ล่าสุดของแต่ละสัญญา)
  const overdueByAccount = Object.fromEntries(
    (accounts ?? []).map((ac) => [ac.account_no, ac.overdue ?? []]),
  );
  const startOf = (a) =>
    resolveStart(overdueByAccount[a.account_no], startDate);
  const oneInterest = (a) =>
    calcInterest(a.principal_amount, startOf(a), asOf, interestRate);
  const rateLabel = `${+(interestRate * 100).toFixed(2)}%`;

  // ═══════════════════════════════════════════
  // หน้า 1 — ข้อมูลลูกค้า
  // ═══════════════════════════════════════════
  let y = 16;

  doc.setFont(FONT, "bold");
  doc.setFontSize(24);
  doc.text("ข้อมูลลูกค้า", ML, y);
  y += 4;
  doc.setDrawColor(50, 50, 50);
  doc.setLineWidth(0.5);
  doc.line(ML, y, MR, y);
  doc.setLineWidth(0.2);
  y += 9;

  // ข้อมูลผู้กู้
  y = drawSectionTitle(doc, ML, y, contentW, "ข้อมูลผู้กู้");
  y = drawField(doc, ML, y, "ชื่อ-นามสกุล", result?.debtor_name);
  y = drawField(doc, ML, y, "รหัสบัตรประชาชน", nationalId);
  y = drawField(doc, ML, y, "Borr Code", result?.borr_code);
  y += 5;

  // เบอร์โทรศัพท์
  if (phones.length > 0) {
    y = drawSectionTitle(doc, ML, y, contentW, "เบอร์โทรศัพท์");
    autoTable(doc, {
      startY: y,
      margin: { left: ML, right: ML },
      theme: "grid",
      styles: {
        font: FONT,
        fontSize: 13,
        textColor: [0, 0, 0],
        lineColor: [80, 80, 80],
        lineWidth: 0.2,
      },
      headStyles: {
        font: FONT,
        fontStyle: "bold",
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        halign: "center",
      },
      head: [["ประเภท", "เบอร์โทรศัพท์"]],
      body: phones.map((p) => [p.usage_label ?? "-", p.telephone_num ?? "-"]),
    });
    y = doc.lastAutoTable.finalY + 7;
  }

  // สัญญาสินเชื่อ
  if (contracts.length > 0) {
    y = drawSectionTitle(doc, ML, y, contentW, "สัญญาสินเชื่อ");
    autoTable(doc, {
      startY: y,
      margin: { left: ML, right: ML },
      theme: "grid",
      styles: {
        font: FONT,
        fontSize: 13,
        textColor: [0, 0, 0],
        lineColor: [80, 80, 80],
        lineWidth: 0.2,
      },
      headStyles: {
        font: FONT,
        fontStyle: "bold",
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      columnStyles: {
        1: { halign: "right" },
        2: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
      },
      head: [
        [
          "เลขที่บัญชี",
          "เงินต้น (บาท)",
          "ดอกเบี้ย SCB (บาท)",
          "ช่วงวันที่",
          "จำนวนวัน",
          `ดอกเบี้ย ONE (บาท) ${rateLabel}`,
        ],
      ],
      body: contracts.map((a) => {
        const start = startOf(a);
        return [
          a.account_no ?? "-",
          thb(a.principal_amount),
          thb(a.interest),
          start ? `${fmtD(start)} - ${fmtD(asOf)}` : "-",
          String(daysFrom(start, asOf)),
          thb(oneInterest(a)),
        ];
      }),
      foot: [
        [
          "รวม",
          thb(
            contracts.reduce((s, a) => s + Number(a.principal_amount || 0), 0),
          ),
          thb(contracts.reduce((s, a) => s + Number(a.interest || 0), 0)),
          "",
          "",
          thb(contracts.reduce((s, a) => s + oneInterest(a), 0)),
        ],
      ],
      footStyles: {
        font: FONT,
        fontStyle: "bold",
        fillColor: [229, 231, 235],
        textColor: [0, 0, 0],
      },
    });
    y = doc.lastAutoTable.finalY + 7;

    // รวมทั้งหมด = เงินต้น + ดอกเบี้ย SCB + ดอกเบี้ย ONE
    const grandAll = contracts.reduce(
      (s, a) =>
        s +
        Number(a.principal_amount || 0) +
        Number(a.interest || 0) +
        oneInterest(a),
      0,
    );
    doc.setFont(FONT, "bold");
    doc.setFontSize(14);
    doc.text(`รวมทั้งหมด: ${thb(grandAll)} บาท`, MR, y, { align: "right" });
    y += 8;
  }

  // หลักประกัน
  if (collaterals.length > 0) {
    y = drawSectionTitle(doc, ML, y, contentW, "หลักประกัน");
    for (const s of collaterals) {
      y = drawField(doc, ML, y, "ประเภท", s.collateral_type);
      y = drawField(doc, ML, y, "ที่ตั้ง", s.location);
      y = drawField(
        doc,
        ML,
        y,
        "ราคาประเมิน",
        `${thb(s.latest_appraisal)} บาท`,
      );
      if (collaterals.length > 1) y += 3;
    }
    y += 5;
  }

  // บริษัท
  y = drawSectionTitle(doc, ML, y, contentW, "บริษัท");
  y = drawField(
    doc,
    ML,
    y,
    "ชื่อบริษัท",
    result?.company_name ?? "บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)",
  );
  drawField(
    doc,
    ML,
    y,
    "ที่อยู่",
    result?.company_address ??
      "89 หมู่ 14 ถ.มิตรภาพ ต.ในเมือง อ.เมืองขอนแก่น จ.ขอนแก่น 40000",
  );

  // ═══════════════════════════════════════════
  // หน้า 2+ — แต่ละ account
  // ═══════════════════════════════════════════
  for (const acc of accounts) {
    doc.addPage();

    const overdue = acc.overdue ?? [];
    const totalPrincipal = overdue.reduce((s, o) => s + (o.principal || 0), 0);
    const totalInterest = overdue.reduce((s, o) => s + (o.interest || 0), 0);
    const totalPenalty = overdue.reduce(
      (s, o) => s + (o.penalty_and_fees || 0),
      0,
    );
    const totalInsurance = overdue.reduce((s, o) => s + (o.insurance || 0), 0);
    const grandTotal = (acc.principal_amount ?? 0) + totalPrincipal;

    let ay = 14;

    // อัตราดอกเบี้ย = (ดอกเบี้ยงวดแรก * 12) / (sum principal + principal_amount)
    const interate = grandTotal
      ? (((overdue[0]?.interest || 0) * 12) / grandTotal) * 100
      : 0;

    // Navy header bar
    doc.setFillColor(NAVY[0], NAVY[1], NAVY[2]);
    doc.rect(ML, ay, contentW, 11, "F");
    doc.setFont(FONT, "bold");
    doc.setFontSize(15);
    doc.setTextColor(255, 255, 255);
    doc.text(
      `Borr Code ${result?.borr_code ?? ""} ${result?.debtor_name}  เลขที่บัญชี ${acc.account_no}`,
      ML + 3,
      ay + 7.5,
    );
    doc.setTextColor(0, 0, 0);
    ay += 14;

    // Gray box — ยอดเงินคงเหลือรวม
    doc.setFillColor(243, 244, 246);
    doc.rect(ML, ay, contentW, 9, "F");
    doc.setDrawColor(180, 180, 180);
    doc.rect(ML, ay, contentW, 9);
    doc.setDrawColor(0, 0, 0);
    doc.setFont(FONT, "normal");
    doc.setFontSize(14);
    const gtLabel = "ยอดเงินต้น: ";
    doc.text(gtLabel, ML + 3, ay + 6);
    doc.setFont(FONT, "bold");
    // doc.text(`${thb(grandTotal)} บาท. ดอกเบี้ย ${interate.toFixed(2)} %`, ML + 3 + doc.getTextWidth(gtLabel), ay + 6)
    doc.text(
      `${thb(grandTotal)} บาท`,
      ML + 3 + doc.getTextWidth(gtLabel),
      ay + 6,
    );
    ay += 13;

    // Rows with running balance
    let running = grandTotal;
    const rows = overdue.map((o) => {
      running -= o.principal || 0;
      return [
        acc.account_no,
        fmtDate(o.due_date),
        { content: thb(o.installment), styles: { halign: "right" } },
        { content: thb(o.principal), styles: { halign: "right" } },
        { content: thb(o.interest), styles: { halign: "right" } },
        // { content: thb(interate), styles: { halign: 'right' } },
        { content: thb(o.penalty_and_fees), styles: { halign: "right" } },
        { content: thb(o.insurance), styles: { halign: "right" } },
        { content: thb(running), styles: { halign: "right" } },
      ];
    });

    const totalRow = [
      {
        content: "รวม (Total)",
        colSpan: 2,
        styles: {
          halign: "center",
          fontStyle: "bold",
          fillColor: [229, 231, 235],
        },
      },
      { content: "", styles: { fillColor: [229, 231, 235] } },
      {
        content: thb(totalPrincipal),
        styles: {
          halign: "right",
          fontStyle: "bold",
          fillColor: [229, 231, 235],
        },
      },
      {
        content: thb(totalInterest),
        styles: {
          halign: "right",
          fontStyle: "bold",
          fillColor: [229, 231, 235],
        },
      },
      // { content: thb(interate), styles: { halign: 'right', fontStyle: 'bold', fillColor: [229, 231, 235] } },
      {
        content: thb(totalPenalty),
        styles: {
          halign: "right",
          fontStyle: "bold",
          fillColor: [229, 231, 235],
        },
      },
      {
        content: thb(totalInsurance),
        styles: {
          halign: "right",
          fontStyle: "bold",
          fillColor: [229, 231, 235],
        },
      },
      { content: "", styles: { fillColor: [229, 231, 235] } },
    ];

    autoTable(doc, {
      startY: ay,
      margin: { left: ML, right: ML },
      theme: "grid",
      styles: {
        font: FONT,
        fontSize: 12,
        textColor: [0, 0, 0],
        lineColor: [80, 80, 80],
        lineWidth: 0.2,
      },
      headStyles: {
        font: FONT,
        fontStyle: "bold",
        fillColor: [229, 231, 235],
        textColor: [0, 0, 0],
        halign: "center",
        lineWidth: 0.2,
      },
      head: [
        [
          "เลขที่บัญชี",
          "วันที่ครบกำหนดชำระ",
          "ค่างวด",
          "เงินต้น",
          "ดอกเบี้ย",
          "ดอกเบี้ยผิดนัด",
          "ค่าเบี้ยประกัน",
          "เงินต้นคงเหลือ",
        ],
      ],
      body: rows,
      foot: [totalRow],
      footStyles: { font: FONT, textColor: [0, 0, 0], lineWidth: 0.2 },
    });
  }

  // ═══════════════════════════════════════════
  // หน้าถัดไป — ข้อมูลหลักประกันและคดีความ (จาก item)
  // ═══════════════════════════════════════════
  if (item) {
    doc.addPage();
    y = 16;

    doc.setFont(FONT, "bold");
    doc.setFontSize(24);
    doc.text("ข้อมูลหลักประกันและคดีความ", ML, y);
    y += 4;
    doc.setDrawColor(50, 50, 50);
    doc.setLineWidth(0.5);
    doc.line(ML, y, MR, y);
    doc.setLineWidth(0.2);
    y += 9;

    const itemPhones = item?.phone ?? [];

    y = drawSectionTitle(doc, ML, y, contentW, "ข้อมูลลูกค้า");
    y = drawLabelValueTable(doc, y, [
      ["เลขสัญญา", item?.borr_code ?? "-"],
      ["ชื่อ-นามสกุล", item?.name ?? "-"],
      [
        "โทรศัพท์",
        itemPhones.length > 0
          ? itemPhones
              .map(
                (p) =>
                  `• ${p.party_locaion_usage ?? "-"} : ${p.telephone_num ?? "-"}`,
              )
              .join("\n")
          : "-",
      ],
    ]);

    // ── หลักประกัน (mortgage) ──
    const mortgages = item?.mortgage ?? [];
    if (mortgages.length > 0) {
      mortgages.forEach((m, i) => {
        y = ensureSpace(doc, y, 20);
        y = drawSectionTitle(
          doc,
          ML,
          y,
          contentW,
          `หลักประกันลำดับที่ ${i + 1}`,
        );
        y = drawLabelValueTable(doc, y, [
          ["ประเภทหลักประกัน", m.collateral_type ?? "-"],
          ["โครงการ", m.project_name ?? "-"],
          ["ที่อยู่หลักประกัน", m.collateral_address ?? "-"],
          ["เลขโฉนด / เลขที่ดิน", m.license_document_number ?? "-"],
          [
            "ขนาดพื้นที่",
            m.area_size
              ? `${m.area_size} ไร่ ${m.area_square_meter ?? 0} ตร.ม.`
              : "-",
          ],
          ["ราคาประเมิน", thb(m.latest_estimated_price)],
          ["เจ้าของกรรมสิทธิ์", m.owner_name ?? "-"],
          ["เบอร์โทรนิติบุคคล/กรรมการหมู่บ้าน", m.juristic_phone ?? "-"],
          ["หมายเลขผู้ใช้ไฟ", m.electric_meter_no ?? "-"],
          ["หมายเลขผู้ใช้น้ำ", m.water_meter_no ?? "-"],
        ]);
      });
    }

    // ── คดีความ (lawsuit) ──
    const lawsuits = item?.lawsuit ?? [];
    if (lawsuits.length > 0) {
      y = ensureSpace(doc, y, 20);
      y = drawSectionTitle(doc, ML, y, contentW, "คดีความ");
      lawsuits.forEach((l, i) => {
        y = ensureSpace(doc, y, 50);
        y = drawSubTitle(doc, ML, y, `คดีความที่ ${i + 1}`);
        y = drawLabelValueTable(doc, y, [
          ["เลขบัญชี", l.account_number ?? "-"],
          ["ศาล", l.court ?? "-"],
          ["หน่วยงาน", l.agency ?? "-"],
          [
            "เลขคดีดำ/แดง",
            `${l.black_case_num || "-"} / ${l.red_case_num || "-"}`,
          ],
          ["ทนาย", l.lawyer_name ?? "-"],
          ["สถานะคดี", l.description ?? "-"],
          ["บันทึกล่าสุด", l.mark ?? "-"],
        ]);
      });
    }

    // ── ยอดหนี้รวม ──
    const itemPrincipal = Number(item?.principle_amount || 0);
    const itemInterest = Number(item?.total_interest_amount || 0);
    const itemTotal = itemPrincipal + itemInterest;
    const minimumPay = (itemPrincipal * 5) / 100 / 12;
    const balloonPay = (itemPrincipal * 2) / 100;

    y = ensureSpace(doc, y, 60);
    y = drawSectionTitle(doc, ML, y, contentW, "ยอดหนี้รวม");
    y = drawLabelValueTable(doc, y, [
      ["เงินต้น", `${thb(itemPrincipal)} บาท`],
      ["ดอกเบี้ย", `${thb(itemInterest)} บาท`],
      [
        "รวมทั้งหมด",
        {
          content: `${thb(itemTotal)} บาท`,
          styles: { textColor: [192, 0, 0], fontStyle: "bold" },
        },
      ],
      ["ผ่อนขั้นต่ำ 5% ต่อปี", `${thb(minimumPay)} บาท`],
      ["เงินก้อน 2%", `${thb(balloonPay)} บาท`],
      ["เงินที่ลูกค้าชำระมา", `${thb(item?.refunded)} บาท`],
    ]);

    // ── เงื่อนไขที่ 1: ขายทรัพย์ ──
    const hasSaleCondition = [
      item?.transfer_date,
      item?.move_out_date,
      item?.housing_common_fee,
      item?.housing_purchase_difference,
      item?.price_before,
    ].some((v) => v !== null && v !== undefined && v !== "" && Number(v) !== 0);

    if (hasSaleCondition) {
      y = ensureSpace(doc, y, 60);
      y = drawSectionTitle(doc, ML, y, contentW, "เงื่อนไขที่ 1: ขายทรัพย์");
      const diff =
        (Number(item.housing_purchase_difference) || 0) -
        (Number(item.price_before) || 0);
      y = drawLabelValueTable(doc, y, [
        ["วันนัดโอน", fmtD(item.transfer_date)],
        ["วันย้ายออก", fmtD(item.move_out_date)],
        ["ค่าส่วนกลาง", `${thb(item.housing_common_fee)} บาท`],
        ["ค่าส่วนต่าง", `${thb(item.housing_purchase_difference)} บาท`],
        ["ชำระค่าส่วนต่าง", `${thb(item.price_before)} บาท`],
        ["ค่าส่วนต่างคงเหลือ", `${thb(diff)} บาท`],
      ]);
    }

    // ── เงื่อนไขที่ 2: ผ่อนชำระ ──
    const hasInstallmentCondition = [
      item?.appointment_date,
      item?.minimum_installment,
      item?.advance_payment,
      item?.recommended_installment,
    ].some((v) => v !== null && v !== undefined && v !== "" && Number(v) !== 0);

    if (hasInstallmentCondition) {
      y = ensureSpace(doc, y, 50);
      y = drawSectionTitle(doc, ML, y, contentW, "เงื่อนไขที่ 2: ผ่อนชำระ");
      y = drawLabelValueTable(doc, y, [
        ["วันนัด", fmtD(item.appointment_date)],
        ["ค่างวดขั้นต่ำ", `${thb(item.minimum_installment)} บาท`],
        ["ชำระเงินก้อน (4 เดือนล่วงหน้า)", `${thb(item.advance_payment)} บาท`],
        ["ค่าผ่อนที่แนะนำ", `${thb(item.recommended_installment)} บาท`],
      ]);
    }

    // ── หมายเหตุจากเฮีย ──
    if (item?.mark) {
      y = ensureSpace(doc, y, 20);
      doc.setFont(FONT, "bold");
      doc.setFontSize(14);
      doc.setTextColor(192, 0, 0);
      const lines = doc.splitTextToSize(
        `หมายเหตุจากเฮีย: ${item.mark}`,
        contentW,
      );
      doc.text(lines, ML, y);
      doc.setTextColor(0, 0, 0);
      y += lines.length * 6 + 2;
    }

    // ── หมายเหตุผู้ปฏิบัติงาน ──
    if (operatorNote) {
      y = ensureSpace(doc, y, 20);
      doc.setFont(FONT, "bold");
      doc.setFontSize(14);
      const lines = doc.splitTextToSize(
        `หมายเหตุผู้ปฏิบัติงาน: ${operatorNote}`,
        contentW,
      );
      doc.text(lines, ML, y);
    }
  }

  // วันที่พิมพ์ (พ.ศ.) มุมขวาบนของทุกหน้า
  const pageCount = doc.internal.getNumberOfPages();
  const now = dayjs();
  const printedOn = `วันที่พิมพ์ ${now.date()}/${now.month() + 1}/${now.year() + 543}`;
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFont(FONT, "normal");
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(printedOn, MR, 10, { align: "right" });
  }

  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  window.open(url);
}
